export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

/**
 * Sur ces routes, un 401 signifie « identifiant, code TOTP ou mot de passe
 * courant invalide », pas « session expirée ». Déclencher la déconnexion
 * globale y renvoyait l'opérateur à l'écran de login sans message d'erreur :
 * le formulaire était démonté avant d'avoir pu afficher la raison du refus.
 */
const CREDENTIAL_PATHS = new Set([
  '/auth/login',
  '/auth/mfa/verify',
  '/auth/mfa/enrollment/confirm',
  '/auth/mfa/recovery-codes/regenerate',
  '/auth/password/change',
])

export class HttpError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Corps JSON brut de la réponse d'erreur, si présent (détails métier). */
    public body?: unknown,
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

export async function httpRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const hasBody = options.body !== undefined && options.body !== null
  const isForm = options.body instanceof FormData
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...(hasBody && !isForm ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  })
  if (!response.ok) {
    let body: { error?: string; message?: string } = {}
    // Corps non-JSON (proxy, page d'erreur HTML) : le statut suffit.
    try { body = await response.json() } catch { /* corps illisible */ }
    const error = new HttpError(response.status, body.error ?? 'UNKNOWN_ERROR', body.message ?? `HTTP ${response.status}`, body)
    if (response.status === 401 && !CREDENTIAL_PATHS.has(path)) {
      window.dispatchEvent(new CustomEvent('trafficplay:auth-required'))
    }
    throw error
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function httpBlob(path: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include' })
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new CustomEvent('trafficplay:auth-required'))
    throw new HttpError(response.status, 'DOWNLOAD_FAILED', `HTTP ${response.status}`)
  }
  return response.blob()
}

export const http = {
  // Les GET alimentés par une invalidation WebSocket doivent toujours relire
  // le serveur. Le cache HTTP peut sinon rendre la même grille après un
  // GRID_UPDATED et faire croire que l'événement temps réel a été perdu.
  get: <T>(path: string) => httpRequest<T>(path, { cache: 'no-store' }),
  post: <T>(path: string, body?: unknown) => httpRequest<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  }),
  patch: <T>(path: string, body: unknown) => httpRequest<T>(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  put: <T>(path: string, body: unknown) => httpRequest<T>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  }),
  delete: <T>(path: string) => httpRequest<T>(path, { method: 'DELETE' }),
}
